// Executive Command Center - Main Entry Point
// Split into multiple files to avoid Babel stack overflow

import { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";

import AccessDenied from "./command-center/AccessDenied";
import HealthScore from "./command-center/HealthScore";
import MarginEngine from "./command-center/MarginEngine";
import PressureMap from "./command-center/PressureMap";
import CashForesight from "./command-center/CashForesight";
import RiskRadar from "./command-center/RiskRadar";

export default function ExecutiveCommandCenter() {
  const { user } = useAuth();
  const canAccess = user?.role === "COO" || user?.role === "Admin";

  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [margin, setMargin] = useState(null);
  const [pressure, setPressure] = useState(null);
  const [cash, setCash] = useState(null);
  const [risk, setRisk] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const results = await Promise.all([
        apiClient.get("/command-center/health-score"),
        apiClient.get("/command-center/margin-intelligence"),
        apiClient.get("/command-center/pressure-map"),
        apiClient.get("/command-center/cash-foresight"),
        apiClient.get("/command-center/risk-radar")
      ]);
      setHealth(results[0].data);
      setMargin(results[1].data);
      setPressure(results[2].data);
      setCash(results[3].data);
      setRisk(results[4].data);
      setLastUpdated(new Date());
    } catch (e) {
      toast.error("Failed to load command center data");
    }
    setLoading(false);
  }, [canAccess]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (mounted && canAccess) {
        await fetchData();
      }
    };
    load();
    return () => { mounted = false; };
  }, [canAccess, fetchData]);

  if (!canAccess) return <AccessDenied />;

  return (
    <div className="space-y-6" data-testid="executive-command-center">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>Executive Command Center</h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time operational intelligence</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <p className="text-xs text-muted-foreground">
              Updated {lastUpdated.toLocaleTimeString()}
            </p>
          )}
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <HealthScore data={health} loading={loading} />
        <RiskRadar data={risk} loading={loading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MarginEngine data={margin} loading={loading} />
        <PressureMap data={pressure} loading={loading} />
      </div>

      <CashForesight data={cash} loading={loading} />
    </div>
  );
}
